import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { platformAnalytics, platformMarketing } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { RequirePlatform } from "./guards";
import { Shell, Card, H1 } from "./ui";

function Inner() {
  const { logout } = useAuth();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [m, setM] = useState(null);
  useEffect(() => { platformAnalytics().then(setD).catch(() => {}); platformMarketing().then(setM).catch(() => {}); }, []);

  const ttvRow = (label, v) => (
    <div className="flex justify-between text-sm border-b border-white/5 py-2"><span className="text-white/60">{label}</span><span className="font-semibold">{v == null ? "—" : `${v} days`}</span></div>
  );

  return (
    <Shell>
      <div className="flex justify-between items-center">
        <div><div className="text-[#00D26A] text-xs tracking-[0.3em] uppercase mb-1">Platform Console</div><H1>Analytics</H1></div>
        <div className="flex gap-4 text-sm items-center">
          <Link to="/platform" className="text-white/60 hover:text-[#00D26A]">Tenants</Link>
          <Link to="/platform/billing" className="text-white/60 hover:text-[#00D26A]">Billing</Link>
          <button onClick={async () => { await logout(); nav("/login"); }} className="text-white/60 hover:text-red-400">Sign out</button>
        </div>
      </div>
      {!d ? <p className="text-white/40 mt-8">Loading…</p> : (
        <div className="space-y-6 mt-8">
          {m && (
            <Card data-testid="marketing-card">
              <div className="flex items-center justify-between mb-4"><h2 className="font-semibold">Marketing & demo funnel</h2>
                <span className="text-xs text-white/40">{m.contact_total} sales enquiries · {m.demo_ai_used} demo AI generations</span></div>
              <div className="space-y-2" data-testid="marketing-funnel-steps">
                {m.funnel.map((s) => (
                  <div key={s.step} data-testid={`mkt-${s.step}`}>
                    <div className="flex justify-between text-xs mb-1"><span className="text-white/70">{s.label}</span><span className="text-white/50">{s.count} · {s.pct}%</span></div>
                    <div className="h-6 bg-white/5 rounded overflow-hidden"><div className="h-full bg-[#00D26A]/70 flex items-center px-2 text-[11px] text-black font-semibold" style={{ width: `${Math.max(s.pct, 3)}%` }}>{s.pct}%</div></div>
                  </div>
                ))}
              </div>
              {m.sources.length > 0 && (
                <div className="mt-5">
                  <div className="text-xs uppercase tracking-wide text-white/40 mb-2">Trials by source</div>
                  <div className="flex flex-wrap gap-2" data-testid="mkt-sources">
                    {m.sources.map((s) => <span key={s.source} className="text-xs bg-white/5 border border-white/10 rounded px-2 py-1 text-white/70">{s.source} <span className="text-[#00D26A] font-semibold">{s.count}</span></span>)}
                  </div>
                </div>
              )}
              {m.contact_leads.length > 0 && (
                <div className="mt-5">
                  <div className="text-xs uppercase tracking-wide text-white/40 mb-2">Recent sales enquiries</div>
                  <div className="space-y-1" data-testid="mkt-contact-leads">
                    {m.contact_leads.slice(0, 6).map((l) => (
                      <div key={l.lead_id} className="flex justify-between text-xs border-b border-white/5 py-1.5">
                        <span className="text-white/80">{l.name} · {l.email}</span>
                        <span className="text-white/40">{l.company || l.source_page || "—"}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          )}
          <Card data-testid="funnel-card">
            <div className="flex items-center justify-between mb-4"><h2 className="font-semibold">Conversion funnel</h2>
              {d.first_value_days != null && <span className="text-xs text-[#00D26A]">Average customer reaches first value in {d.first_value_days} days</span>}</div>
            <div className="space-y-2" data-testid="funnel-steps">
              {d.funnel.map((s) => (
                <div key={s.step} data-testid={`funnel-${s.step}`}>
                  <div className="flex justify-between text-xs mb-1"><span className="text-white/70">{s.label}</span><span className="text-white/50">{s.count} · {s.pct}%</span></div>
                  <div className="h-6 bg-white/5 rounded overflow-hidden"><div className="h-full bg-[#00D26A]/70 flex items-center px-2 text-[11px] text-black font-semibold" style={{ width: `${Math.max(s.pct, 3)}%` }}>{s.pct}%</div></div>
                </div>
              ))}
            </div>
          </Card>

          <div className="grid md:grid-cols-2 gap-6">
            <Card data-testid="ttv-card">
              <h2 className="font-semibold mb-3">Time to value</h2>
              {ttvRow("Signup → first design", d.time_to_value.signup_to_first_design)}
              {ttvRow("Signup → embed installed", d.time_to_value.signup_to_embed)}
              {ttvRow("Signup → first lead", d.time_to_value.signup_to_first_lead)}
              {ttvRow("Signup → paid conversion", d.time_to_value.signup_to_paid)}
            </Card>
            <Card>
              <h2 className="font-semibold mb-3">Totals</h2>
              <div className="text-4xl font-bold">{d.total_signups}</div>
              <div className="text-white/40 text-xs uppercase mt-1">Total trials started</div>
            </Card>
          </div>

          <Card data-testid="cohorts-card">
            <h2 className="font-semibold mb-4">Weekly cohorts</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-white/40 text-xs uppercase text-left border-b border-white/10">
                  <th className="py-2 pr-3">Week commencing</th><th className="pr-3">Signups</th><th className="pr-3">Onboarded</th><th className="pr-3">Embed</th><th className="pr-3">Leads</th><th className="pr-3">Converted</th></tr></thead>
                <tbody>
                  {d.cohorts.length === 0 ? <tr><td colSpan={6} className="py-3 text-white/40">No signups yet.</td></tr> :
                    d.cohorts.map((c) => (
                      <tr key={c.week} className="border-b border-white/5" data-testid={`cohort-${c.week}`}>
                        <td className="py-2 pr-3">{c.week}</td><td className="pr-3">{c.signups}</td><td className="pr-3">{c.onboarding}</td>
                        <td className="pr-3">{c.embed}</td><td className="pr-3">{c.leads}</td><td className="pr-3 text-[#00D26A]">{c.converted}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}
    </Shell>
  );
}

export default function PlatformAnalytics() { return <RequirePlatform><Inner /></RequirePlatform>; }
